import { useQuery } from '@tanstack/react-query'
import { repository } from '../data'
import type { ShortageRow } from '../data'
import { BuyLinks } from './BuyLinks'
import { formatDay } from './dates'
import { Badge, EmptyState, Panel, Section } from './primitives'

/**
 * To buy: what released build orders need that the shelf cannot cover,
 * soonest-to-order first, grouped by how it gets cleared — bought, made by a
 * vendor, or built here.
 *
 * One table per group. The arithmetic is in the row (needed, on the shelf,
 * short) rather than asserted: somebody about to spend money on 18 actuators
 * should be able to see where 18 came from.
 */
const GROUP: readonly { readonly kind: ShortageRow['kind']; readonly title: string }[] = [
  { kind: 'buy', title: 'Buy' },
  { kind: 'make_external', title: 'Order from a vendor' },
  { kind: 'make_internal', title: 'Build here' },
]

export function PlanView(): JSX.Element {
  const shortages = useQuery({ queryKey: ['shortages'], queryFn: () => repository.shortages() })
  const rows = shortages.data ?? []

  return (
    <div className="page">
      {shortages.isPending ? <p className="muted">Loading…</p> : null}
      {shortages.isError ? <p className="critical">Could not load what to buy.</p> : null}

      {shortages.isSuccess && rows.length === 0 ? (
        <Panel>
          <EmptyState icon="stock" title="Nothing to buy">
            The shelf covers every released build order. This fills itself the moment one needs something.
          </EmptyState>
        </Panel>
      ) : null}

      {GROUP.map(({ kind, title }) => {
        const group = rows.filter((r) => r.kind === kind)
        if (group.length === 0) return null
        const late = group.filter((r) => r.late).length
        return (
          <Section key={kind} label={`${title} · ${group.length}`} actions={late > 0 ? <Badge tone="critical">{late} late</Badge> : undefined}>
            <Panel>
              <div className="table-scroll">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Item</th>
                      <th className="num">Short</th>
                      <th className="num">Shelf / needed</th>
                      <th>{kind === 'make_internal' ? 'Start by' : 'Order by'}</th>
                      <th>Supplier</th>
                      <th aria-label="Links" />
                    </tr>
                  </thead>
                  <tbody>
                    {group.map((row) => (
                      <tr key={row.typeId}>
                        <td>
                          <strong style={{ fontWeight: 500 }}>{row.typeName}</strong>
                          {row.leadTimeUnknown ? <div className="warn" style={{ fontSize: 'var(--font-size-sm)' }}>No lead time on file</div> : null}
                        </td>
                        <td className="num figure">{row.short}</td>
                        <td className="num muted">
                          {row.onHand} / {row.required}
                        </td>
                        <td>
                          {row.late ? <Badge tone="critical">{formatDay(row.orderBy)} — passed</Badge> : formatDay(row.orderBy)}
                        </td>
                        <td className="muted">
                          {row.supplier}
                          {row.partNumber ? <div className="serial" style={{ fontSize: 'var(--font-size-sm)' }}>{row.partNumber}</div> : null}
                        </td>
                        <td>
                          <BuyLinks links={row.purchaseLinks} compact />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Panel>
          </Section>
        )
      })}
    </div>
  )
}
